import { useMemo } from 'react';

import { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { CALL_INTENTS, CALL_OUTCOMES } from '@/consts/calls';
import { cn } from '@/lib/utils';

type Call = ApiSchemas['CallRead'];

type CallData = { analysis?: Call['analysis'] };

interface CallBadgeProps {
  className?: string;
  call: CallData;
}

export function CallCategoryBadge({ call, className }: CallBadgeProps) {
  const label = useMemo(() => {
    const intent = call.analysis?.intent;
    if (!intent) return '';
    return CALL_INTENTS.find((i) => i.value === intent.name)?.label;
  }, [call.analysis?.intent]);
  if (!label) return null;
  return (
    <Badge variant='secondary' className={cn('flex items-center gap-1.5 rounded-full', className)}>
      {label}
    </Badge>
  );
}

export function CallOutcomeBadge({ call, className }: CallBadgeProps) {
  const result = useMemo(() => {
    const value = call.analysis?.outcome?.result;
    return CALL_OUTCOMES.find((i) => i.value === value)?.label;
  }, [call.analysis?.outcome?.result]);

  if (!result) return null;

  return (
    <Badge variant='secondary' className={cn('rounded-full', OUTCOME_ACHIEVED_CLASS_NAMES[result], className)}>
      {result}
    </Badge>
  );
}

export function CallOutcomeClientBadge({ call, className }: CallBadgeProps) {
  const result = useMemo(() => {
    const value = call.analysis?.outcome_client?.result;
    return CALL_OUTCOMES.find((i) => i.value === value)?.label;
  }, [call.analysis?.outcome_client?.result]);

  if (!result) return null;

  return (
    <Badge variant='secondary' className={cn('rounded-full', OUTCOME_ACHIEVED_CLASS_NAMES[result], className)}>
      {result}
    </Badge>
  );
}

const OUTCOME_ACHIEVED_CLASS_NAMES: Record<string, string> = {
  Excused: 'bg-card text-muted-foreground border-muted border-dashed border-2',
  Success: 'bg-green-500/10 text-green-500',
  Failure: 'bg-red-500/10 text-red-500',
};

export function CallHumanRequestedBadge({ call, className }: CallBadgeProps) {
  const label = useMemo(() => {
    const value = call.analysis?.human_request?.result;
    if (!value) return null;
    if (value === 'NONE') return 'No';
    return 'Yes';
  }, [call.analysis?.human_request?.result]);
  if (!label) return null;
  return (
    <Badge
      variant='secondary'
      className={cn(
        'bg-muted text-muted-foreground rounded-full',
        label === 'Yes' && 'bg-orange-500/10 text-orange-500',
        className
      )}
    >
      {label}
    </Badge>
  );
}
